/**
 * ============================================================================
 * useCalibrationTrayQuery — Phase 6e frontend (Equipment & Validation).
 * Owner: Claude 3 (Frontend & UX).
 * Purpose: TanStack Query hook for the Calibration Tray. Returns three
 *          buckets (DUE_THIS_WEEK / OVERDUE / RECENTLY_COMPLETED) so we can
 *          flip tabs without a re-fetch.
 * Endpoint: GET /api/v1/equipment/calibrations
 * ============================================================================
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';
import type {
  EquipmentCriticality,
  GampClass,
} from '../EquipmentWorkbench/useEquipmentQuery';

export type CalibrationTaskBucket =
  | 'DUE_THIS_WEEK'
  | 'OVERDUE'
  | 'RECENTLY_COMPLETED';

export interface CalibrationTask {
  task_id: string;
  equipment_id: string;
  equipment_tag: string;
  equipment_name: string;
  criticality: EquipmentCriticality;
  gamp_class: GampClass;
  /** Negative when the task is overdue; positive when it's due in the future. */
  days_until_due: number;
  due_at_utc: string;
  last_calibration_at_utc: string | null;
  technician_hint: string | null;
  bucket: CalibrationTaskBucket;
}

export interface CalibrationTrayResponse {
  items: CalibrationTask[];
  counts: Record<CalibrationTaskBucket, number>;
}

export function useCalibrationTrayQuery(): UseQueryResult<CalibrationTrayResponse> {
  return useQuery<CalibrationTrayResponse>({
    queryKey: ['equipment', 'calibrationTray'],
    queryFn: ({ signal }) =>
      apiFetch<CalibrationTrayResponse>('/api/v1/equipment/calibrations', {
        signal,
      }),
  });
}
